/**
 * @file Core User & Roles System
 * Provides lightweight, offline-first user management and role-based access control.
 * This is a UI convenience system that helps create intuitive user interfaces.
 * @since 2025-08-25
 */

const { ref, computed, reactive } = Vue;
import { useConfig } from '../../configs/composables/config.js';
import { useErrorHandler, useNotifications } from '../../core/errors.js';

// Global state for user system
let _options = null;
let _isInitialized = false;
let _initializeResolve = null;
let _initializeReject = null;
const _initializePromise = new Promise((resolve, reject) => {
  _initializeResolve = resolve;
  _initializeReject = reject;
});

export const GUEST_USER = {
  id: 'guest',
  name: 'Guest User',
  email: '',
};

// Define internal _user config for user data
const userConfig = useConfig({
  name: '_user',
  defaultValue: { ...GUEST_USER, loggedIn: false },
  schema: {
    type: 'object',
    properties: {
      id: { type: 'string' },
      name: { type: 'string' },
      email: { type: 'string' },
      groups: { type: 'array', items: { type: 'string' } },
      loggedIn: { type: 'boolean' },
    },
    required: ['id', 'name', 'email', 'groups', 'loggedIn'],
    editableBy: [],
  },
});

const user = Vue.computed(() => {
  return { ...GUEST_USER, groups: [], ...userConfig.value };
});

const isLoading = ref(false);
const groupRequestHandler = ref(null);

// Built-in providers
const providers = {
  guest: {
    getCurrentUser: async () => ({ ...GUEST_USER, groups: user.value.groups }),
  },

  mock: userData => ({
    getCurrentUser: async () => ({ ...GUEST_USER, ...userData }),
  }),
};

/**
 * Set user to guest state
 */
async function setToGuest() {
  if (!_options.allowLogout) {
    throw new Error('Logout is disabled in this application');
  }
  const guestUser = await providers.guest.getCurrentUser();
  return updateUserData({ ...guestUser, loggedIn: false });
}

/**
 * Initialize user data
 * @returns {Promise<void>} Promise that resolves when initialization is complete
 */
async function initializeUserState() {
  try {
    await userConfig.getInstance();

    console.log('User state initialized successfully');
    _initializeResolve();

    if (_options.autoLogin) {
      await loadUserData();
    }
  } catch (error) {
    console.error('Failed to initialize user state:', error);
    _initializeReject(error);
  }
}

/**
 * Load user data from provider
 * @returns {Promise<void>} Promise that resolves when user data is loaded
 */
async function loadUserData() {
  if (!_options?.getCurrentUser) {
    return setToGuest();
  }

  isLoading.value = true;

  try {
    const userData = await _options.getCurrentUser();

    if (!userData || typeof userData !== 'object') {
      throw new Error('Provider returned invalid user data');
    }

    const userObj = {
      ...userData,
      loggedIn: true,
    };

    return updateUserData(userObj);
  } catch (error) {
    console.error('Failed to load user data:', error);

    const { handleError } = useErrorHandler();
    const { warning } = useNotifications();

    await handleError(error, { operation: 'loadUser' });
    warning('Failed to load user data, retry later.');

    return setToGuest();
  } finally {
    isLoading.value = false;
  }
}

/**
 * Update user data
 * @param {object} userData - User data to update
 * @returns {Promise<object>} Updated user data
 */
async function updateUserData(userData) {
  await _initializePromise;
  await userConfig.replace(userData);
  return userData;
}

/**
 * Initialize the user system
 * @param {object} options - Configuration options
 */
export function initUser(options = {}) {
  if (_isInitialized) {
    throw new Error(
      'User system already initialized. Use useUser() to access the instance.'
    );
  }

  const defaultOptions = {
    getCurrentUser: null,
    groups: {},
    groupRequestHandler: null,
    autoLogin: true,
    allowLogout: true,
    allowGroupRequests: true,
  };

  // Store configuration with defaults
  _options = { ...defaultOptions, ...options };
  groupRequestHandler.value = _options.groupRequestHandler;
  _isInitialized = true;

  // Start async initialization
  initializeUserState();
}

let _user = null;

/**
 * Core useUser composable
 * @returns {object} User composable with reactive properties and methods
 */
export function useUser() {
  const ensureInitialized = () => _initializePromise;

  if (_user !== null) {
    return _user;
  }

  // Computed roles from groups
  const computedRoles = computed(() => {
    if (!_options.groups || !user.value?.groups) return [];

    const allRoles = new Set();
    user.value.groups.forEach(groupId => {
      const groupDef = _options.groups[groupId];
      if (groupDef && groupDef.roles) {
        groupDef.roles.forEach(role => allRoles.add(role));
      }
    });
    return [...allRoles];
  });

  // Role checking methods (now based on computed roles)
  const hasRole = computed(() => {
    return role => computedRoles.value.includes(role);
  });

  const hasAnyRole = computed(() => {
    return roles => roles.some(role => hasRole.value(role));
  });

  const hasAllRoles = computed(() => {
    return roles => roles.every(role => hasRole.value(role));
  });

  // Group management methods
  const grantGroup = async groups => {
    await ensureInitialized();

    const groupList = Array.isArray(groups) ? groups : [groups];
    const currentGroups = new Set(user.value.groups);

    groupList.forEach(groupId => {
      if (!_options.groups[groupId]) {
        console.warn(`Unknown group '${groupId}'`);
      }
      currentGroups.add(groupId);
    });

    await userConfig.set('groups', [...currentGroups]);
  };

  const removeGroup = async groups => {
    await ensureInitialized();

    const groupList = Array.isArray(groups) ? groups : [groups];
    const currentGroups = new Set(user.value.groups);

    groupList.forEach(groupId => currentGroups.delete(groupId));

    await userConfig.set('groups', [...currentGroups]);
  };

  // Available groups from configuration
  const availableGroups = computed(() => {
    return Object.entries(_options.groups).map(([groupId, groupDef]) => ({
      id: groupId,
      ...groupDef,
    }));
  });

  // User's current groups
  const userGroups = computed(() => user.value?.groups ?? []);

  const joinGroup = async (groupId, metadata) => {
    await ensureInitialized();

    if (!_options.groupRequestHandler) {
      // Direct join if no handler
      await grantGroup(groupId);
      return { status: 'granted', message: `Joined group ${groupId}` };
    }

    const result = await _options.groupRequestHandler.handleGroupRequest({
      userId: user.value.id,
      groupId,
      action: 'join',
      metadata,
    });

    if (result.status === 'granted') {
      await grantGroup(groupId);
    }

    return result;
  };

  const leaveGroup = async groupId => {
    await ensureInitialized();

    if (!_options.groupRequestHandler) {
      // Direct leave if no handler
      await removeGroup(groupId);
      return { status: 'granted', message: `Left group ${groupId}` };
    }

    const result = await _options.groupRequestHandler.handleGroupRequest({
      userId: user.value.id,
      groupId,
      action: 'leave',
    });

    if (result.status === 'granted') {
      await removeGroup(groupId);
    }

    return result;
  };

  const cancelGroupRequest = async requestId => {
    await ensureInitialized();

    if (!_options.groupRequestHandler?.cancelRequest) {
      return false;
    }

    return await _options.groupRequestHandler.cancelRequest(requestId);
  };

  const refreshPendingGroupRequests = async () => {
    await ensureInitialized();

    if (!_options.groupRequestHandler?.userPendingRequests) {
      return false;
    }

    // With reactive API, we just return the current reactive value
    // The reactive property automatically updates when data changes
    return _options.groupRequestHandler.userPendingRequests.value;
  };

  // Store and return reactive user object
  _user = reactive({
    // Core user properties
    id: computed(() => user.value?.id),
    name: computed(() => user.value?.name),
    email: computed(() => user.value?.email),
    groups: computed(() => user.value?.groups),
    roles: computedRoles, // Computed from groups
    loggedIn: computed(() => user.value?.loggedIn),
    isLoading: computed(() => isLoading.value),

    // Role checking methods (now computed from groups)
    hasRole: hasRole.value,
    hasAnyRole: hasAnyRole.value,
    hasAllRoles: hasAllRoles.value,

    // Group management methods (primary API)
    grantGroup,
    removeGroup,
    joinGroup,
    leaveGroup,
    cancelGroupRequest,
    groupRequestHandler,
    refreshPendingGroupRequests,

    // Group properties
    availableGroups,
    userGroups,
    pendingGroupRequests: computed(
      () => _options.groupRequestHandler?.userPendingRequests?.value || []
    ),

    // System methods
    refresh: loadUserData,
    login: loadUserData,
    logout: setToGuest,
    ensureInitialized: () => _initializePromise,

    // Configuration options
    allowLogout: computed(() => _options?.allowLogout ?? true),
    allowGroupRequests: computed(() => _options?.allowGroupRequests ?? true),
  });

  return _user;
}

// Built-in group request handlers
const createDirectGroupHandler = () => ({
  async handleGroupRequest({ groupId, action }) {
    return {
      status: 'granted',
      message: `${action === 'join' ? 'Joined' : 'Left'} ${groupId}`,
    };
  },
});

const createPasswordGroupHandler = passwordConfig => {
  // passwordConfig: { groupId: 'passwordHash', groupId2: 'hash2', ... }

  // Password prompt utility
  const promptForPassword = async groupId => {
    return new Promise(resolve => {
      const password = prompt(`Enter password for ${groupId}:`);
      resolve(password);
    });
  };

  // Simple password hash utility (using built-in crypto if available)
  const hashPassword = async password => {
    if (typeof crypto !== 'undefined' && crypto.subtle) {
      const encoder = new TextEncoder();
      const data = encoder.encode(password);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    }
    // Fallback for environments without crypto.subtle
    return btoa(password);
  };

  return {
    async handleGroupRequest({ groupId, action, metadata }) {
      if (action === 'leave') {
        return { status: 'granted', message: `Left ${groupId}` };
      }

      // Check password if required
      const requiredHash = passwordConfig[groupId];
      if (requiredHash) {
        let password = metadata?.password;
        if (!password) {
          password = await promptForPassword(groupId);
        }

        if (!password) {
          return { status: 'denied', message: 'Password required' };
        }

        const hash = await hashPassword(password);

        if (hash !== requiredHash) {
          return { status: 'denied', message: 'Invalid password' };
        }
      }

      return { status: 'granted', message: `Joined ${groupId}` };
    },
  };
};

// Export providers and handlers
export { providers, createDirectGroupHandler, createPasswordGroupHandler };
